import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import api from "../../../../../../utils/axiosInstance";
import { toast, confirmDialog } from "../../../../../../components/Toast/toast";
import { btn } from "../../../../../../components/Button/buttonStyles";

// Tick-box selection plus a confirmed bulk delete for one of a job's file
// tables. `endpoint` is the bulk-delete route for that file type.
export const useBulkFileDelete = ({ jobId, files, endpoint, onDeleted }) => {
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);

  // Drop ticks for rows that are gone after a refresh.
  useEffect(() => {
    setSelected((prev) => prev.filter((id) => files.some((f) => f.id === id)));
  }, [files]);

  const allSelected = files.length > 0 && selected.length === files.length;

  const toggle = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleAll = () => setSelected(allSelected ? [] : files.map((f) => f.id));

  const deleteSelected = async () => {
    const count = selected.length;
    const label = count === 1 ? "this file" : `these ${count} files`;
    if (!(await confirmDialog(`Are you sure you want to delete ${label}?`))) return;

    try {
      setDeleting(true);
      await api.post(endpoint, { job_id: jobId, ids: selected }, { withCredentials: true });
      toast.success(count === 1 ? "File deleted successfully" : `${count} files deleted`);
      setSelected([]);
      onDeleted?.();
    } catch (error) {
      console.error("Bulk delete error:", error);
      toast.error(error.response?.data?.message || "Failed to delete files");
    } finally {
      setDeleting(false);
    }
  };

  return { selected, allSelected, deleting, toggle, toggleAll, deleteSelected };
};

export const DeleteSelectedButton = ({ count, deleting, onClick }) => (
  <button
    onClick={onClick}
    disabled={count === 0 || deleting}
    title={count === 0 ? "Tick the files you want to delete" : undefined}
    className={btn("dangerSoft", "sm")}
  >
    <Trash2 className="w-3.5 h-3.5" />
    {deleting ? "Deleting..." : `Delete${count ? ` (${count})` : ""}`}
  </button>
);

export const RowCheckbox = ({ label, ...props }) => (
  <input
    type="checkbox"
    aria-label={label}
    className="h-4 w-4 cursor-pointer rounded accent-brand-600 disabled:cursor-not-allowed"
    {...props}
  />
);
